import React, { useState, useMemo, useEffect } from "react";
import {
    Modal,
    Box,
    Dialog,
} from "@mui/material";
import { CloseOutlined, Search, AccessTime, CheckCircleOutlined } from "@mui/icons-material";
import { CustomizeTextField, style } from "../templates/TemplateTable";
import useAuthFetch from "../../hooks/useAuthFetch";
import toast from "react-hot-toast";

const ViewDetialsModal = ({ defaultUtility, setOpenErr, setErrMsg, startDate = '', endDate = '' }) => {

    const customFetch = useAuthFetch();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [tableData, setTableData] = useState([]);

    const [selectedApps, setSelectedApps] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [triggerText, setTriggerText] = useState(false);
    const [timestamp, setTimestamp] = useState("");
    const [executionResult, setExecutionResult] = useState(null);

    const [minutes, setMinutes] = useState("");
    const [seconds, setSeconds] = useState("");

    const [selectedUtility, setSelectedUtility] = useState(defaultUtility);
    const [endpointType, setEndpointType] = useState(defaultUtility);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage] = useState(11);

    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;

    const totalPages = Math.ceil(tableData.length / rowsPerPage);
    const [statusFilter, setStatusFilter] = useState("All");
    const [currentStatusFilter, setCurrentStatusFilter] = useState("All");
    const [appNoSearch, setAppNoSearch] = useState("");

    useEffect(() => {
        if (open && selectedUtility) {
            fetchUtilityDetails(selectedUtility);
        }
    }, [open, selectedUtility, startDate, endDate]);

    const fetchUtilityDetails = async (utility) => {
        try {
            if (!utility) return;

            const url = `${process.env.REACT_APP_API_URL}/v1/dashboard/application/getJourneyDashboardCountAPI`;

            const cleanStart = startDate && startDate.includes('T') ? startDate.split('T')[0] : startDate;
            const cleanEnd = endDate && endDate.includes('T') ? endDate.split('T')[0] : endDate;

            const bodyContent = {
                status: utility,
                startTime: cleanStart ? `${cleanStart}T00:00:00.000Z` : "",
                endTime: cleanEnd ? `${cleanEnd}T23:59:59.999Z` : ""
            };

            console.log("Modal Details Payload Sent:", bodyContent);

            const { response, status, message } = await customFetch(
                url,
                "POST",
                bodyContent
            );

            if (status !== 200) {
                setOpenErr(true);
                setErrMsg(message || "Failed to load tracking data logs");
                setTableData([]);
            } else {
                setTableData(response.data || response || []);
            }
        } catch (error) {
            setOpenErr(true);
            setErrMsg(error instanceof Error ? error.message : String(error));
            console.error("Error in fetching journey utility breakdown:", error);
        }
    };
    const filteredData = tableData.filter((row) => {
        const matchesAppNo = row.applicationNo
            ?.toLowerCase()
            .includes(appNoSearch.toLowerCase());

        const matchesStatus =
            statusFilter === "All" || row.journeyStatus === statusFilter;

        return matchesAppNo && matchesStatus;
    });
    const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);
    useEffect(() => {
        setEndpointType(selectedUtility);
    }, [selectedUtility]);

    useEffect(() => {
        if (defaultUtility) {
            setSelectedUtility(defaultUtility);
            setEndpointType(defaultUtility);
            setSelectedApps([]);
            setSearchTerm("");
            setMinutes("");
            setSeconds("");
        }
    }, [defaultUtility]);

    const uniqueStatuses = [...new Set(tableData.map(item => item.status))].filter(Boolean);

    const uniqueCurrentStatuses = [...new Set(tableData.map(item => item.currentStatus || 'In-progress'))].filter(Boolean);

    const handleOpen = () => {
        setSelectedUtility(defaultUtility);
        setEndpointType(defaultUtility);
        setSelectedApps([]);
        setSearchTerm("");
        setMinutes("");
        setSeconds("");
        setOpen(true);
    };


    const handleClose = () => {
        setOpen(false);
        setEndpointType("");
        setSelectedApps([]);
        setSearchTerm("");
        setTriggerText(false);
        setMinutes("");
        setSeconds("");
        setTimestamp("");
    };

    useEffect(() => {
        if (open && selectedUtility) {
            fetchUtilityDetails(selectedUtility);
        }
    }, [open, selectedUtility]);



    const utilityLabels = {

        "UDS-PDF": "PDF Not Uploaded to UDS",
        "S3-PDF": "PDF Not Uploaded to S3",
        "S3-REC": "Recording Not Uploaded to S3",
        "SCREENSHOT": "Journey Screenshots Not Generated",
        "KYP-GEN": "KYP Photo Not Generated",
        "DIGI-UP": "KYP Digi Status is Not Updated",
        "UDS-UP": "KYP UDS Status is Not Updated",
        "MCA-UP": "KYP MCA Status is Not Updated"
    };

    return (
        <>
            <button
                onClick={() =>
                    handleOpen()
                }
                className="group flex items-center justify-center bg-gray-50 text-gray-500 
             rounded-md border border-gray-200 p-2 
             hover:bg-white hover:text-black hover:border-gray-400 
             transition-all duration-300 ease-in-out hover:px-4"
            >
                <svg
                    xmlns="http://w3.org"
                    width="18" height="18"
                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                >
                    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                    <circle cx="12" cy="12" r="3" />
                </svg>

                <span className="max-w-0 overflow-hidden whitespace-nowrap text-[10px] font-bold uppercase tracking-widest 
                   transition-all duration-300 ease-in-out group-hover:max-w-[100px] group-hover:ml-2">
                    View Details
                </span>
            </button>


            <Dialog open={open} onClose={handleClose} closeAfterTransition={false}>
                <Modal open={open} onClose={handleClose}>
                    <Box sx={{ ...style, width: 1200, height: '90%', display: 'flex', flexDirection: 'column', padding: 5, backgroundColor: '#f9fafb' }} className="rounded-xl relative p-6">
                        <button
                            className="absolute top-4 right-4 z-50"
                            onClick={() => {
                                setExecutionResult(null);
                                handleClose();
                            }}
                        >
                            <CloseOutlined className="w-5 h-5 text-gray-450" />
                        </button>
                        <div className="flex flex-row items-center justify-between gap-4 mb-4 pb-2 border-b border-gray-100">
                            <p className="bg-blue-50 text-blue-600 text-[10px] font-bold px-2 py-0.5 rounded border border-blue-100 uppercase tracking-widest">
                                {utilityLabels[selectedUtility] || "Details"}
                            </p>

                            <div className="flex items-center gap-3">
                                <select
                                    value={statusFilter}
                                    onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                                    className="w-40 px-2 py-1.5 border border-gray-300 rounded text-xs bg-white focus:ring-1 focus:ring-blue-500 outline-none text-gray-700 font-medium"
                                >
                                    <option value="All">All Statuses</option>
                                    {Array.from(new Set(tableData.map(row => row.journeyStatus)))
                                        .filter(Boolean)
                                        .map((status) => (
                                            <option key={status} value={status}>
                                                {status}
                                            </option>
                                        ))}
                                </select>

                                <input
                                    type="text"
                                    placeholder="Search App No..."
                                    value={appNoSearch}
                                    onChange={(e) => { setAppNoSearch(e.target.value); setCurrentPage(1); }}
                                    className="w-48 px-3 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 outline-none placeholder:text-gray-400"
                                />

                                {(appNoSearch || statusFilter !== "All" || currentStatusFilter !== "All") && (
                                    <button
                                        onClick={() => { setAppNoSearch(""); setStatusFilter("All"); setCurrentStatusFilter("All"); }}
                                        className="text-[10px] text-blue-600 hover:underline font-medium"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        </div>

                        {loading ? (
                            <p>Loading details...</p>
                        ) : tableData.length > 0 ? (
                            <>
                                <div className="flex-1 overflow-hidden flex flex-col border border-gray-200 rounded-lg">
                                    <div className="overflow-auto w-full">
                                        <table className="min-w-full divide-y divide-gray-200 table-auto">
                                            <thead className="bg-gray-50 sticky top-0 z-10 shadow-[0_1px_0_0_rgba(0,0,0,0.1)]">
                                                <tr>
                                                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 tracking-wider">Application No</th>
                                                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 tracking-wider">Journey ID</th>
                                                    <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 tracking-wider">Status</th>
                                                </tr>
                                            </thead>

                                            <tbody className="divide-y divide-gray-100">
                                                {currentRows.map((row, index) => {
                                                    return (
                                                        <tr key={index} className="hover:bg-blue-50/30 transition-colors duration-150 group">
                                                            <td className="py-3 px-4 text-sm text-gray-700 font-medium whitespace-nowrap">{row.applicationNo}</td>
                                                            <td className="py-3 px-4 text-sm text-gray-500 whitespace-nowrap">{row.journeyId}</td>
                                                            <td className="py-3 px-4 text-sm text-gray-500 whitespace-nowrap">{row.journeyStatus}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                                    <div className="flex-1 flex justify-between sm:hidden">
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                            disabled={currentPage === 1}
                                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                        >
                                            Previous
                                        </button>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                            disabled={currentPage === totalPages}
                                            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                        >
                                            Next
                                        </button>
                                    </div>
                                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                        <div>
                                            <p className="text-sm text-gray-700">
                                                Showing <span className="font-medium">{indexOfFirstRow + 1}</span> to{' '}
                                                <span className="font-medium">{Math.min(indexOfLastRow, tableData.length)}</span> of{' '}
                                                <span className="font-medium">{tableData.length}</span> results
                                            </p>
                                        </div>
                                        <div>
                                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                                <button
                                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                    disabled={currentPage === 1}
                                                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                                >
                                                    <span className="sr-only">Previous</span>
                                                    &larr;
                                                </button>

                                                {(() => {
                                                    let startPage = Math.max(1, currentPage - 2);
                                                    let endPage = Math.min(totalPages, startPage + 4);

                                                    if (endPage - startPage < 4) {
                                                        startPage = Math.max(1, endPage - 4);
                                                    }

                                                    const pages = [];
                                                    for (let i = startPage; i <= endPage; i++) {
                                                        pages.push(
                                                            <button
                                                                key={i}
                                                                onClick={() => setCurrentPage(i)}
                                                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === i
                                                                    ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                                                    : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                                                                    }`}
                                                            >
                                                                {i}
                                                            </button>
                                                        );
                                                    }
                                                    return pages;
                                                })()}

                                                <button
                                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                    disabled={currentPage === totalPages}
                                                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                                >
                                                    <span className="sr-only">Next</span>
                                                    &rarr;
                                                </button>
                                            </nav>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <p className="text-gray-500 italic">No data available for {utilityLabels[selectedUtility]}.</p>
                        )}

                    </Box>
                </Modal>
            </Dialog>

        </>

    );
};
export default ViewDetialsModal;
